import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FacilityDocumentsService } from 'src/app/shared/services/facility-documents.service';
import { FacilityDocumentStatus } from 'src/app/shared/services/facility-documents.service';
import { FeatureFlagsService } from 'src/app/shared/services/featureFlags.service';

@Component({
  selector: 'app-facility-card',
  templateUrl: './facility-card.component.html',
  styleUrls: ['./facility-card.component.scss'],
})
export class FacilityCardComponent implements OnInit {
  @Input() facility: any;
  @Input() primaryFacilityId: any;
  @Input() userId: any;
  @Input() isReadOnly: boolean = false;
  @Output() switch = new EventEmitter<any>();

  status: FacilityDocumentStatus;
  showUsersPopup: boolean = false;
  EXTERNAL_VETTING_COMPANY_NAME = 'Caretria';

  constructor(private facilityDocumentsService: FacilityDocumentsService, public featureFlagsService: FeatureFlagsService) { }

  ngOnInit() {
    this.status = this.facilityDocumentsService.evaluateStatus(this.facility, [], true);
  }

  get hasStateLicenseIssue(): boolean {
    return Boolean(this.status?.state_license_missing || this.status?.state_license_expired);
  }

  get hasDeaIssue(): boolean {
    return Boolean(this.status?.dea_license_missing || this.status?.dea_license_expired);
  }

  get stateLicenseExpiringSoon(): boolean {
    if (this.hasStateLicenseIssue) {
      return false;
    }

    const expirationDate = this.parseDate(this.facility?.state_license_expiration);
    if (!expirationDate) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneMonthFromNow = new Date(today);
    oneMonthFromNow.setDate(oneMonthFromNow.getDate() + 30);

    return expirationDate >= today && expirationDate <= oneMonthFromNow;
  }

  get showLicenseAlert(): boolean {
    return this.hasStateLicenseIssue || this.stateLicenseExpiringSoon || this.hasDeaIssue;
  }

  get licenseAlertClass(): string {
    return this.hasStateLicenseIssue || this.hasDeaIssue
      ? 'state-license-danger-icon'
      : 'state-license-warning-icon';
  }

  get licenseAlertTooltipText(): string {
    const messages: string[] = [];

    if (this.status?.state_license_missing) {
      messages.push("This facility's state license is missing.");
    }

    if (this.status?.state_license_expired) {
      messages.push("This facility's state license is expired.");
    }

    if (this.stateLicenseExpiringSoon) {
      messages.push("This facility's state license is expiring within 30 days.");
    }

    if (this.status?.dea_license_missing) {
      messages.push("This facility's DEA license is missing.");
    }

    if (this.status?.dea_license_expired) {
      messages.push("This facility's DEA license is expired.");
    }

    return messages.join('<br/>');
  }

  get unvettedTooltipText(): string {
    const externalVettingStatus = (this.facility?.external_vetting || '').toLowerCase();
    if (externalVettingStatus === 'new') {
      return 'Facility created, our team will review the facility and documents within 1-2 business days.';
    }

    if (externalVettingStatus === 'pending') {
      return `Facility has been sent to ${this.EXTERNAL_VETTING_COMPANY_NAME}, waiting on account setup completion`;
    }

    if (externalVettingStatus) {
      return `External vetting status: ${this.facility.external_vetting}`;
    }

    return 'This facility is not yet vetted. This normally applies to newly registered accounts. Vetting normally takes 1-2 business days assuming valid account information and license.';
  }

  private parseDate(dateValue: string): Date | null {
    if (!dateValue) {
      return null;
    }

    const parsedDate = new Date(dateValue);
    if (!Number.isNaN(parsedDate.getTime())) {
      parsedDate.setHours(0, 0, 0, 0);
      return parsedDate;
    }

    const dateParts = dateValue.split('/');
    if (dateParts.length !== 3) {
      return null;
    }

    const [month, day, year] = dateParts.map(part => Number(part));
    if (!month || !day || !year) {
      return null;
    }

    const normalizedDate = new Date(year, month - 1, day);
    normalizedDate.setHours(0, 0, 0, 0);
    return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate;
  }

  onSwitch() {
    this.switch.emit({ facility_id: this.facility.id, user_id: this.userId });
  }
}
